import React, { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { FileText, Download, Upload, RefreshCw, Settings } from 'lucide-react';
import { Button } from '../ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/Card';
import toast from 'react-hot-toast';

interface ConversionOptions {
  delimiter: string;
  hasHeader: boolean;
  skipEmptyLines: boolean;
  trimWhitespace: boolean;
  customHeaders: string[];
}

export const CsvToJsonTool: React.FC = () => {
  const { t } = useTranslation();
  const [csvInput, setCsvInput] = useState('');
  const [jsonOutput, setJsonOutput] = useState('');
  const [options, setOptions] = useState<ConversionOptions>({
    delimiter: ',',
    hasHeader: true,
    skipEmptyLines: true,
    trimWhitespace: true,
    customHeaders: []
  });
  const [customHeadersInput, setCustomHeadersInput] = useState('');
  const [isConverting, setIsConverting] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const parseCSV = (csv: string, options: ConversionOptions): any[] => {
    const lines = csv.split('\n');
    const result: any[] = [];
    
    // Filter empty lines if option is set
    const filteredLines = options.skipEmptyLines 
      ? lines.filter(line => line.trim() !== '') 
      : lines;
    
    if (filteredLines.length === 0) {
      return [];
    }

    // Parse CSV with proper delimiter handling
    const parseCSVLine = (line: string): string[] => {
      const result: string[] = [];
      let current = '';
      let inQuotes = false;
      
      for (let i = 0; i < line.length; i++) {
        const char = line[i];
        const nextChar = line[i + 1];
        
        if (char === '"') {
          if (inQuotes && nextChar === '"') {
            // Escaped quote
            current += '"';
            i++; // Skip next quote
          } else {
            // Toggle quote state
            inQuotes = !inQuotes;
          }
        } else if (char === options.delimiter && !inQuotes) {
          // Field separator
          result.push(options.trimWhitespace ? current.trim() : current);
          current = '';
        } else {
          current += char;
        }
      }
      
      // Add the last field
      result.push(options.trimWhitespace ? current.trim() : current);
      return result;
    };

    // Get headers
    let headers: string[];
    let dataStartIndex = 0;
    
    if (options.hasHeader && filteredLines.length > 0) {
      headers = parseCSVLine(filteredLines[0]);
      dataStartIndex = 1;
    } else if (options.customHeaders.length > 0) {
      headers = options.customHeaders;
    } else {
      // Generate default headers
      const firstRow = parseCSVLine(filteredLines[0]);
      headers = firstRow.map((_, index) => `column_${index + 1}`);
    }

    // Parse data rows
    for (let i = dataStartIndex; i < filteredLines.length; i++) {
      const row = parseCSVLine(filteredLines[i]);
      const obj: any = {};
      
      headers.forEach((header, index) => {
        obj[header] = row[index] || '';
      });
      
      result.push(obj);
    }

    return result;
  };

  const convertCsvToJson = async () => {
    if (!csvInput.trim()) {
      toast.error('Please enter CSV data');
      return;
    }

    setIsConverting(true);
    
    try {
      // Parse custom headers if provided
      const customHeaders = customHeadersInput.trim() 
        ? customHeadersInput.split(',').map(h => h.trim()).filter(h => h)
        : [];
      
      const currentOptions = { ...options, customHeaders };
      const jsonData = parseCSV(csvInput, currentOptions);
      const jsonString = JSON.stringify(jsonData, null, 2);
      
      setJsonOutput(jsonString);
      toast.success('CSV converted to JSON successfully');
    } catch (error) {
      console.error('Conversion error:', error);
      toast.error('Failed to convert CSV to JSON');
      setJsonOutput('');
    } finally {
      setIsConverting(false);
    }
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      if (file.type !== 'text/csv' && !file.name.endsWith('.csv')) {
        toast.error('Please select a CSV file');
        return;
      }

      const reader = new FileReader();
      reader.onload = (e) => {
        const content = e.target?.result as string;
        setCsvInput(content);
      };
      reader.readAsText(file);
    }
  };

  const downloadJson = () => {
    if (!jsonOutput) {
      toast.error('No JSON data to download');
      return;
    }

    const blob = new Blob([jsonOutput], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'converted-data.json';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const loadSampleData = () => {
    const sampleCsv = `name,age,city,country
John Doe,30,New York,USA
Jane Smith,25,London,UK
Bob Johnson,35,Toronto,Canada
Alice Brown,28,Sydney,Australia`;
    setCsvInput(sampleCsv);
  };

  const clearAll = () => {
    setCsvInput('');
    setJsonOutput('');
    setCustomHeadersInput('');
  };

  const delimiters = [
    { value: ',', label: 'Comma (,)' },
    { value: ';', label: 'Semicolon (;)' },
    { value: '\t', label: 'Tab' },
    { value: '|', label: 'Pipe (|)' }
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <FileText className="w-5 h-5" />
            <span>{t('common.csvToJsonConverter')}</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-2">
            <Button
              onClick={() => fileInputRef.current?.click()}
              variant="outline"
            >
              <Upload className="w-4 h-4 mr-2" />
              {t('common.uploadCsvFile')}
            </Button>
            <Button
              onClick={loadSampleData}
              variant="outline"
            >
              Load Sample Data
            </Button>
            <Button
              onClick={clearAll}
              variant="outline"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Clear All
            </Button>
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv,text/csv"
            onChange={handleFileUpload}
            className="hidden"
          />
        </CardContent>
      </Card>

      {/* Conversion Options */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Settings className="w-5 h-5" />
            <span>Conversion Options</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Delimiter
                </label>
                <select
                  value={options.delimiter}
                  onChange={(e) => setOptions({ ...options, delimiter: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                >
                  {delimiters.map((delimiter) => (
                    <option key={delimiter.value} value={delimiter.value}>
                      {delimiter.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="space-y-2">
                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={options.hasHeader}
                    onChange={(e) => setOptions({ ...options, hasHeader: e.target.checked })}
                    className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  />
                  <span className="text-sm">First row contains headers</span>
                </label>
                
                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={options.skipEmptyLines}
                    onChange={(e) => setOptions({ ...options, skipEmptyLines: e.target.checked })}
                    className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  />
                  <span className="text-sm">Skip empty lines</span>
                </label>
                
                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={options.trimWhitespace}
                    onChange={(e) => setOptions({ ...options, trimWhitespace: e.target.checked })}
                    className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  />
                  <span className="text-sm">Trim whitespace</span>
                </label>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Custom Headers (comma-separated)
              </label>
              <textarea
                value={customHeadersInput}
                onChange={(e) => setCustomHeadersInput(e.target.value)}
                placeholder="header1, header2, header3..."
                className="w-full h-20 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white text-sm"
              />
              <p className="text-xs text-gray-500 mt-1">
                Leave empty to use first row as headers or auto-generate column names
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Input/Output */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* CSV Input */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span>{t('common.csvInput')}</span>
              <Button
                onClick={convertCsvToJson}
                disabled={!csvInput.trim() || isConverting}
                variant="primary"
                size="sm"
              >
                {isConverting ? t('common.converting') : t('common.convertToJson')}
              </Button>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <textarea
              value={csvInput}
              onChange={(e) => setCsvInput(e.target.value)}
              placeholder="Paste your CSV data here or upload a file..."
              className="w-full h-64 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white font-mono text-sm"
            />
          </CardContent>
        </Card>

        {/* JSON Output */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span>{t('common.jsonOutput')}</span>
              {jsonOutput && (
                <Button
                  onClick={downloadJson}
                  variant="outline"
                  size="sm"
                >
                  <Download className="w-3 h-3 mr-1" />
                  Download
                </Button>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <textarea
              value={jsonOutput}
              readOnly
              placeholder="JSON output will appear here..."
              className="w-full h-64 px-3 py-2 border border-gray-300 rounded-md bg-gray-50 dark:border-gray-600 dark:bg-gray-800 dark:text-white font-mono text-sm"
            />
          </CardContent>
        </Card>
      </div>

      {/* Usage Guide */}
      <Card>
        <CardHeader>
          <CardTitle>Usage Guide</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-sm">
            <div>
              <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-2">CSV Format Tips:</h4>
              <ul className="space-y-1 text-gray-600 dark:text-gray-400">
                <li>• Use quotes for fields containing commas or newlines</li>
                <li>• Escape quotes within fields by doubling them ("")</li>
                <li>• Ensure consistent number of columns per row</li>
                <li>• First row can contain column headers</li>
                <li>• Empty fields are converted to empty strings</li>
              </ul>
            </div>
            <div>
              <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-2">Conversion Options:</h4>
              <ul className="space-y-1 text-gray-600 dark:text-gray-400">
                <li>• Choose appropriate delimiter for your data</li>
                <li>• Enable header detection for proper field names</li>
                <li>• Use custom headers to override column names</li>
                <li>• Skip empty lines to clean up data</li>
                <li>• Trim whitespace for cleaner output</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
